import {
  useEffect,
  useMemo,
  useState,
} from "react"

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"

import AddRoundedIcon from "@mui/icons-material/AddRounded"
import DeleteIcon from "@mui/icons-material/Delete"

import { useNavigate } from "react-router-dom"

import MainLayout from "../../layouts/MainLayout"

import {
  approvePurchaseRequest,
  cancelPurchaseRequest,
  createPurchaseRequest,
  getPurchaseRequestById,
  getPurchaseRequestFormData,
  getPurchaseRequests,
  rejectPurchaseRequest,
  submitPurchaseRequest,
  updatePurchaseRequest,
} from "../../services/purchaseRequestService"


// =========================================================
// Helpers généraux
// =========================================================

const today = () =>
  new Date().toISOString().slice(0, 10)


const normalizeStatus = (value) =>
  String(value || "DRAFT")
    .trim()
    .toUpperCase()


const normalizeType = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()


const safeJsonParse = (
  value,
  fallback = {}
) => {
  try {
    return JSON.parse(
      value || "{}"
    )
  } catch {
    return fallback
  }
}


const getCurrentRole = () => {
  const user = safeJsonParse(
    localStorage.getItem(
      "kongosys_user"
    ),
    {}
  )

  return String(
    localStorage.getItem(
      "kongosys_role"
    ) ||
      user.role ||
      ""
  )
    .trim()
    .toLowerCase()
}


const getArray = (
  data,
  ...keys
) => {
  for (const key of keys) {
    if (
      Array.isArray(
        data?.[key]
      )
    ) {
      return data[key]
    }
  }

  return []
}


const RAW_PURCHASE_PRODUCT_TYPES = new Set([
  "RAW_MATERIAL",
  "RAW_MATERIALS",
  "MATERIAL",
  "MATERIALS",
  "INGREDIENT",
  "INGREDIENTS",
  "COMPONENT",
  "COMPONENTS",
  "PACKAGING",
  "PACKAGING_MATERIAL",
  "CONSUMABLE",
  "CONSUMABLES",
  "SUPPLY",
  "SUPPLIES",
  "SEMI_FINISHED",
  "SEMI_FINISHED_GOOD",
])


const FINISHED_PURCHASE_PRODUCT_TYPES = new Set([
  "FINISHED_GOOD",
  "FINISHED_GOODS",
  "MERCHANDISE",
  "RETAIL_GOOD",
  "RESALE_GOOD",
  "TRADED_GOOD",
])


const isRawMaterialWarehouse = (
  warehouseType
) => {
  const type =
    normalizeType(
      warehouseType
    )

  return [
    "RAW_MATERIAL",
    "RAW_MATERIALS",
  ].includes(type)
}


const isFinishedGoodsWarehouse = (
  warehouseType
) => {
  const type =
    normalizeType(
      warehouseType
    )

  return [
    "FINISHED_GOOD",
    "FINISHED_GOODS",
  ].includes(type)
}


const isMixedWarehouse = (
  warehouseType
) => {
  const type =
    normalizeType(
      warehouseType
    )

  return [
    "MIXED",
    "TRANSIT",
  ].includes(type)
}


const explicitFalse = (
  value
) => {
  if (
    value === false ||
    value === 0
  ) {
    return true
  }

  return [
    "false",
    "0",
    "no",
    "non",
  ].includes(
    String(
      value ?? ""
    )
      .trim()
      .toLowerCase()
  )
}


const isPurchaseEligible = (
  product
) => {
  if (
    explicitFalse(
      product?.purchase_eligible
    ) ||
    explicitFalse(
      product?.can_be_purchased
    )
  ) {
    return false
  }

  return true
}


const productMatchesWarehouse = (
  product,
  warehouse
) => {
  if (
    !product ||
    !warehouse ||
    !isPurchaseEligible(
      product
    )
  ) {
    return false
  }

  const warehouseType =
    normalizeType(
      warehouse.warehouse_type
    )

  const productType =
    normalizeType(
      product.product_type
    )

  const compatibleTypes =
    Array.isArray(
      product.compatible_warehouse_types
    )
      ? product.compatible_warehouse_types.map(
          normalizeType
        )
      : []

  if (
    compatibleTypes.length > 0
  ) {
    return compatibleTypes.includes(
      warehouseType
    )
  }

  if (
    isMixedWarehouse(
      warehouseType
    )
  ) {
    return true
  }

  if (
    isRawMaterialWarehouse(
      warehouseType
    )
  ) {
    if (!productType) {
      return true
    }

    return (
      RAW_PURCHASE_PRODUCT_TYPES.has(
        productType
      ) ||
      !FINISHED_PURCHASE_PRODUCT_TYPES.has(
        productType
      )
    )
  }

  if (
    isFinishedGoodsWarehouse(
      warehouseType
    )
  ) {
    if (!productType) {
      return true
    }

    return FINISHED_PURCHASE_PRODUCT_TYPES.has(
      productType
    )
  }

  return true
}


const formatProductType = (
  productType
) => {
  const type =
    normalizeType(
      productType
    )

  const labels = {
    RAW_MATERIAL:
      "Matière première",
    RAW_MATERIALS:
      "Matière première",
    MATERIAL:
      "Matière",
    MATERIALS:
      "Matière",
    INGREDIENT:
      "Ingrédient",
    INGREDIENTS:
      "Ingrédient",
    COMPONENT:
      "Composant",
    COMPONENTS:
      "Composant",
    PACKAGING:
      "Emballage",
    PACKAGING_MATERIAL:
      "Emballage",
    CONSUMABLE:
      "Consommable",
    CONSUMABLES:
      "Consommable",
    SUPPLY:
      "Fourniture",
    SUPPLIES:
      "Fourniture",
    SEMI_FINISHED:
      "Semi-fini",
    SEMI_FINISHED_GOOD:
      "Semi-fini",
    FINISHED_GOOD:
      "Produit fini",
    FINISHED_GOODS:
      "Produit fini",
    MERCHANDISE:
      "Marchandise",
    RETAIL_GOOD:
      "Marchandise",
    RESALE_GOOD:
      "Marchandise",
    TRADED_GOOD:
      "Marchandise",
  }

  return (
    labels[type] ||
    productType ||
    "-"
  )
}


const defaultItem = {
  product_id: "",
  requested_quantity: 1,
  estimated_unit_cost: 0,
  notes: "",
}


const createDefaultFormData = () => ({
  organization_id: 1,

  warehouse_id: "",

  required_date:
    today(),

  reason: "",

  notes: "",

  items: [
    {
      ...defaultItem,
    },
  ],
})


// =========================================================
// Labels
// =========================================================

const STATUS_LABELS = {
  DRAFT: "Brouillon",
  SUBMITTED: "Soumise",
  APPROVED: "Approuvée",
  REJECTED: "Rejetée",
  CANCELLED: "Annulée",
  CONVERTED: "Convertie en BC",
  CONVERTED_TO_PO:
    "Convertie en BC",
}


const statusColor = (
  status
) => {
  const normalized =
    normalizeStatus(status)

  switch (normalized) {
    case "APPROVED":
    case "CONVERTED":
    case "CONVERTED_TO_PO":
      return "success"

    case "SUBMITTED":
      return "warning"

    case "REJECTED":
    case "CANCELLED":
      return "error"

    case "DRAFT":
    default:
      return "default"
  }
}


// =========================================================
// Page
// =========================================================


function MetricCard({
  label,
  value,
  caption,
  accent = "#2563eb",
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        minWidth: 0,
        minHeight: 100,
        borderRadius: 3.5,
        borderLeft: "4px solid",
        borderLeftColor: accent,
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          fontWeight: 800,
          letterSpacing: 0.25,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          mt: 0.35,
          fontSize: { xs: 24, md: 28 },
          lineHeight: 1.15,
          fontWeight: 900,
          color: "#0f172a",
        }}
      >
        {value}
      </Typography>

      {caption ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.6 }}
        >
          {caption}
        </Typography>
      ) : null}
    </Paper>
  )
}

function PurchaseRequestsPage() {
  const navigate =
    useNavigate()

  const currentRole =
    getCurrentRole()

  const canApprove = [
    "platform_admin",
    "super_admin",
  ].includes(currentRole)


  // =======================================================
  // States principaux
  // =======================================================

  const [
    requests,
    setRequests,
  ] = useState([])

  const [
    products,
    setProducts,
  ] = useState([])

  const [
    warehouses,
    setWarehouses,
  ] = useState([])

  const [
    organizations,
    setOrganizations,
  ] = useState([])

  const [
    loading,
    setLoading,
  ] = useState(false)

  const [
    saving,
    setSaving,
  ] = useState(false)

  const [
    open,
    setOpen,
  ] = useState(false)

  const [
    selectedRequest,
    setSelectedRequest,
  ] = useState(null)

  const [
    formData,
    setFormData,
  ] = useState(
    createDefaultFormData()
  )

  const [
    searchTerm,
    setSearchTerm,
  ] = useState("")

  const [page, setPage] = useState(0)
  const rowsPerPage = 5

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("")

  // Revenir à la première page après une recherche ou un filtre.
  useEffect(() => {
    setPage(0)
  }, [searchTerm, statusFilter])

  const [
    viewOpen,
    setViewOpen,
  ] = useState(false)

  const [
    viewData,
    setViewData,
  ] = useState(null)

  const [
    viewLoading,
    setViewLoading,
  ] = useState(false)

  const [
    rejectOpen,
    setRejectOpen,
  ] = useState(false)

  const [
    rejectTarget,
    setRejectTarget,
  ] = useState(null)

  const [
    rejectionReason,
    setRejectionReason,
  ] = useState("")

  const [
    processingId,
    setProcessingId,
  ] = useState(null)

  const [
    snackbar,
    setSnackbar,
  ] = useState({
    open: false,
    severity: "success",
    message: "",
  })


  // =======================================================
  // Snackbar
  // =======================================================

  const showSnackbar = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      severity,
      message,
    })
  }


  // =======================================================
  // Chargement
  // =======================================================

  const loadData = async () => {
    try {
      setLoading(true)

      const [
        list,
        formDataResponse,
      ] = await Promise.all([
        getPurchaseRequests(),
        getPurchaseRequestFormData(),
      ])

      setRequests(
        Array.isArray(list)
          ? list
          : []
      )

      setProducts(
        getArray(
          formDataResponse,
          "products",
          "active_products"
        )
      )

      setWarehouses(
        getArray(
          formDataResponse,
          "warehouses",
          "active_warehouses"
        )
      )

      setOrganizations(
        getArray(
          formDataResponse,
          "organizations",
          "active_organizations"
        )
      )
    } catch (error) {
      console.error(
        error
      )

      showSnackbar(
        error?.response?.data?.detail ||
          error?.message ||
          "Impossible de charger les demandes d'achat.",
        "error"
      )
    } finally {
      setLoading(false)
    }
  }


  useEffect(() => {
    loadData()
  }, [])


  // =======================================================
  // Entrepôt actuellement sélectionné
  // =======================================================

  const selectedWarehouse =
    useMemo(() => {
      return warehouses.find(
        (warehouse) =>
          Number(
            warehouse.id
          ) ===
          Number(
            formData.warehouse_id
          )
      )
    }, [
      warehouses,
      formData.warehouse_id,
    ])


  // =======================================================
  // Produits compatibles avec l'entrepôt
  // =======================================================

  const availableProducts =
    useMemo(() => {
      if (
        !selectedWarehouse
      ) {
        return products.filter(
          isPurchaseEligible
        )
      }

      return products.filter(
        (product) =>
          productMatchesWarehouse(
            product,
            selectedWarehouse
          )
      )
    }, [
      products,
      selectedWarehouse,
    ])


  // =======================================================
  // Helpers affichage
  // =======================================================

  const warehouseLabel = (
    warehouseId
  ) => {
    const warehouse =
      warehouses.find(
        (item) =>
          Number(
            item.id
          ) ===
          Number(
            warehouseId
          )
      )

    if (!warehouse) {
      return warehouseId
        ? `Entrepôt #${warehouseId}`
        : "-"
    }

    return warehouse.code
      ? `${warehouse.code} - ${warehouse.name}`
      : warehouse.name
  }


  const productLabel = (
    productId
  ) => {
    const product =
      products.find(
        (item) =>
          Number(
            item.id
          ) ===
          Number(
            productId
          )
      )

    if (!product) {
      return `Produit #${productId}`
    }

    return product.code
      ? `${product.code} - ${product.name}`
      : product.name
  }


  const formatQuantity = (
    quantity,
    unit
  ) => {
    if (
      quantity === null ||
      quantity === undefined
    ) {
      return "-"
    }

    const numeric =
      Number(quantity || 0)

    if (!unit) {
      return numeric
    }

    return `${numeric} ${unit}`
  }


  const formatWarehouseType = (
    warehouseType
  ) => {
    if (
      isRawMaterialWarehouse(
        warehouseType
      )
    ) {
      return "Matières, composants & emballages"
    }

    if (
      isFinishedGoodsWarehouse(
        warehouseType
      )
    ) {
      return "Produits finis / marchandises"
    }

    if (
      normalizeType(
        warehouseType
      ) === "MIXED"
    ) {
      return "Stock mixte"
    }

    if (
      normalizeType(
        warehouseType
      ) === "TRANSIT"
    ) {
      return "Transit"
    }

    return (
      warehouseType ||
      "Non défini"
    )
  }


  // =======================================================
  // Formulaire
  // =======================================================

  const resetForm = () => {
    setSelectedRequest(
      null
    )

    setFormData(
      createDefaultFormData()
    )
  }


  const openAddDialog = () => {
    resetForm()

    setOpen(
      true
    )
  }


  const openEditDialog = async (
    requestId
  ) => {
    try {
      const data =
        await getPurchaseRequestById(
          requestId
        )

      if (
        normalizeStatus(
          data.status
        ) !== "DRAFT"
      ) {
        showSnackbar(
          "Seule une demande en brouillon peut être modifiée.",
          "warning"
        )

        return
      }

      setSelectedRequest(
        data
      )

      setFormData({
        organization_id:
          Number(
            data.organization_id ||
              1
          ),

        warehouse_id:
          data.warehouse_id ||
          "",

        required_date:
          data.required_date
            ? String(
                data.required_date
              ).slice(
                0,
                10
              )
            : today(),

        reason:
          data.reason ||
          "",

        notes:
          data.notes ||
          "",

        items:
          Array.isArray(
            data.items
          ) &&
          data.items.length >
            0
            ? data.items.map(
                (item) => ({
                  product_id:
                    item.product_id ||
                    "",

                  requested_quantity:
                    Number(
                      item.requested_quantity ||
                        1
                    ),

                  estimated_unit_cost:
                    Number(
                      item.estimated_unit_cost ||
                        0
                    ),

                  notes:
                    item.notes ||
                    "",
                })
              )
            : [
                {
                  ...defaultItem,
                },
              ],
      })

      setOpen(
        true
      )
    } catch (error) {
      console.error(
        error
      )

      showSnackbar(
        error?.response?.data?.detail ||
          error?.message ||
          "Impossible de charger cette demande d'achat.",
        "error"
      )
    }
  }


  const handleClose = () => {
    if (saving) {
      return
    }

    setOpen(
      false
    )

    resetForm()
  }


  const handleMainChange = (
    event
  ) => {
    const {
      name,
      value,
    } = event.target

    const numericFields = [
      "organization_id",
      "warehouse_id",
    ]

    if (
      name === "warehouse_id"
    ) {
      setFormData(
        (prev) => ({
          ...prev,

          warehouse_id:
            value === ""
              ? ""
              : Number(value),

          // Changement d'entrepôt :
          // on réinitialise les produits pour éviter
          // une matière première envoyée vers un
          // entrepôt produits finis, et inversement.
          items: [
            {
              ...defaultItem,
            },
          ],
        })
      )

      return
    }

    setFormData(
      (prev) => ({
        ...prev,

        [name]:
          numericFields.includes(
            name
          )
            ? value === ""
              ? ""
              : Number(
                  value
                )
            : value,
      })
    )
  }


  const handleItemChange = (
    index,
    field,
    value
  ) => {
    const numericFields = [
      "product_id",
      "requested_quantity",
      "estimated_unit_cost",
    ]

    setFormData(
      (prev) => ({
        ...prev,

        items:
          prev.items.map(
            (
              item,
              currentIndex
            ) =>
              currentIndex ===
              index
                ? {
                    ...item,

                    [field]:
                      numericFields.includes(
                        field
                      )
                        ? value ===
                          ""
                          ? ""
                          : Number(
                              value
                            )
                        : value,
                  }
                : item
          ),
      })
    )
  }


  const addItem = () => {
    setFormData(
      (prev) => ({
        ...prev,

        items: [
          ...prev.items,

          {
            ...defaultItem,
          },
        ],
      })
    )
  }


  const removeItem = (
    index
  ) => {
    setFormData(
      (prev) => {
        if (
          prev.items.length ===
          1
        ) {
          return prev
        }

        return {
          ...prev,

          items:
            prev.items.filter(
              (
                _,
                currentIndex
              ) =>
                currentIndex !==
                index
            ),
        }
      }
    )
  }


  const estimatedTotal =
    useMemo(() => {
      return formData.items.reduce(
        (
          total,
          item
        ) =>
          total +
          Number(
            item.requested_quantity ||
              0
          ) *
            Number(
              item.estimated_unit_cost ||
                0
            ),

        0
      )
    }, [
      formData.items,
    ])


  // =======================================================
  // Enregistrement
  // =======================================================

  const handleSave = async () => {
    if (
      !formData.warehouse_id
    ) {
      showSnackbar(
        "Sélectionnez un entrepôt.",
        "warning"
      )

      return
    }

    if (
      !formData.reason.trim()
    ) {
      showSnackbar(
        "Précisez le motif de l'achat.",
        "warning"
      )

      return
    }

    const invalidItem =
      formData.items.some(
        (item) =>
          !item.product_id ||
          Number(
            item.requested_quantity
          ) <= 0
      )

    if (invalidItem) {
      showSnackbar(
        "Vérifiez les produits et les quantités demandées.",
        "warning"
      )

      return
    }

    const selectedProductIds =
      formData.items.map(
        (item) =>
          Number(
            item.product_id
          )
      )

    const hasDuplicate =
      new Set(
        selectedProductIds
      ).size !==
      selectedProductIds.length

    if (hasDuplicate) {
      showSnackbar(
        "Le même produit ne doit pas être ajouté plusieurs fois dans la demande.",
        "warning"
      )

      return
    }

    const payload = {
      organization_id:
        Number(
          formData.organization_id ||
            1
        ),

      warehouse_id:
        Number(
          formData.warehouse_id
        ),

      required_date:
        formData.required_date,

      reason:
        formData.reason.trim(),

      notes:
        formData.notes?.trim() ||
        null,

      items:
        formData.items.map(
          (item) => ({
            product_id:
              Number(
                item.product_id
              ),

            requested_quantity:
              Number(
                item.requested_quantity
              ),

            estimated_unit_cost:
              Number(
                item.estimated_unit_cost ||
                  0
              ),

            notes:
              item.notes?.trim() ||
              null,
          })
        ),
    }

    try {
      setSaving(
        true
      )

      if (
        selectedRequest?.id
      ) {
        await updatePurchaseRequest(
          selectedRequest.id,
          payload
        )

        showSnackbar(
          "Demande d'achat modifiée."
        )
      } else {
        await createPurchaseRequest(
          payload
        )

        showSnackbar(
          "Demande d'achat créée."
        )
      }

      setOpen(
        false
      )

      resetForm()

      await loadData()
    } catch (error) {
      console.error(
        error
      )

      showSnackbar(
        error?.response?.data?.detail ||
          error?.message ||
          "Impossible d'enregistrer la demande.",
        "error"
      )
    } finally {
      setSaving(
        false
      )
    }
  }


  // =======================================================
  // Action générique
  // =======================================================

  const runAction = async (
    requestId,
    action,
    successMessage
  ) => {
    try {
      setProcessingId(
        requestId
      )

      await action()

      showSnackbar(
        successMessage
      )

      await loadData()
    } catch (error) {
      console.error(
        error
      )

      showSnackbar(
        error?.response?.data?.detail ||
          error?.message ||
          "Impossible d'effectuer cette opération.",
        "error"
      )
    } finally {
      setProcessingId(
        null
      )
    }
  }


  // =======================================================
  // Voir demande
  // =======================================================

  const openViewDialog = async (
    requestId
  ) => {
    try {
      setViewLoading(
        true
      )

      setViewOpen(
        true
      )

      const data =
        await getPurchaseRequestById(
          requestId
        )

      setViewData(
        data
      )
    } catch (error) {
      console.error(
        error
      )

      setViewOpen(
        false
      )

      showSnackbar(
        error?.response?.data?.detail ||
          error?.message ||
          "Impossible de charger le détail de la demande.",
        "error"
      )
    } finally {
      setViewLoading(
        false
      )
    }
  }


  // =======================================================
  // Soumission
  // =======================================================

  const handleSubmitRequest = (
    requestId
  ) => {
    runAction(
      requestId,

      () =>
        submitPurchaseRequest(
          requestId
        ),

      "Demande soumise pour validation interne."
    )
  }


  // =======================================================
  // Approbation
  // =======================================================

  const handleApprove = async (
    requestId
  ) => {
    if (!canApprove) {
      showSnackbar(
        "Vous n'avez pas l'autorisation d'approuver cette demande.",
        "warning"
      )

      return
    }

    try {
      setProcessingId(
        requestId
      )

      const detail =
        await getPurchaseRequestById(
          requestId
        )

      if (
        !Array.isArray(
          detail.items
        ) ||
        detail.items.length ===
          0
      ) {
        throw new Error(
          "Cette demande ne contient aucun article."
        )
      }

      const items =
        detail.items.map(
          (item) => ({
            item_id:
              Number(
                item.id
              ),

            approved_quantity:
              Number(
                item.requested_quantity ||
                  0
              ),
          })
        )

      await approvePurchaseRequest(
        requestId,
        {
          items,
          notes: null,
        }
      )

      showSnackbar(
        "Demande d'achat approuvée."
      )

      await loadData()
    } catch (error) {
      console.error(
        error
      )

      showSnackbar(
        error?.response?.data?.detail ||
          error?.message ||
          "Impossible d'approuver cette demande.",
        "error"
      )
    } finally {
      setProcessingId(
        null
      )
    }
  }


  // =======================================================
  // Rejet
  // =======================================================

  const openRejectDialog = (
    request
  ) => {
    if (!canApprove) {
      showSnackbar(
        "Vous n'avez pas l'autorisation de rejeter cette demande.",
        "warning"
      )

      return
    }

    setRejectTarget(
      request
    )

    setRejectionReason(
      ""
    )

    setRejectOpen(
      true
    )
  }


  const closeRejectDialog = () => {
    if (
      processingId
    ) {
      return
    }

    setRejectOpen(
      false
    )

    setRejectTarget(
      null
    )

    setRejectionReason(
      ""
    )
  }


  const handleReject = async () => {
    if (
      !rejectTarget?.id
    ) {
      return
    }

    if (
      !rejectionReason.trim()
    ) {
      showSnackbar(
        "Veuillez préciser le motif du rejet.",
        "warning"
      )

      return
    }

    try {
      setProcessingId(
        rejectTarget.id
      )

      await rejectPurchaseRequest(
        rejectTarget.id,
        {
          reason:
            rejectionReason.trim(),
        }
      )

      setRejectOpen(
        false
      )

      setRejectTarget(
        null
      )

      setRejectionReason(
        ""
      )

      showSnackbar(
        "Demande d'achat rejetée."
      )

      await loadData()
    } catch (error) {
      console.error(
        error
      )

      showSnackbar(
        error?.response?.data?.detail ||
          error?.message ||
          "Impossible de rejeter cette demande.",
        "error"
      )
    } finally {
      setProcessingId(
        null
      )
    }
  }


  // =======================================================
  // Annuler
  // =======================================================

  const handleCancelRequest = (
    requestId
  ) => {
    runAction(
      requestId,

      () =>
        cancelPurchaseRequest(
          requestId
        ),

      "Demande d'achat annulée."
    )
  }


  // =======================================================
  // Créer bon de commande
  // =======================================================

  const handleCreatePurchaseOrder = (
    request
  ) => {
    navigate(
      `/purchase-orders?purchase_request_id=${request.id}`,
      {
        state: {
          purchaseRequestId:
            request.id,

          purchaseRequestCode:
            request.code,
        },
      }
    )
  }


  // =======================================================
  // Filtres
  // =======================================================

  const filteredRequests =
    useMemo(() => {
      const query =
        searchTerm
          .toLowerCase()
          .trim()

      return requests.filter(
        (request) => {
          const searchableText = [
            request.code,
            request.request_number,
            request.reason,
            request.warehouse_name,
            request.organization_name,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()

          const matchesSearch =
            !query ||
            searchableText.includes(
              query
            )

          const currentStatus =
            normalizeStatus(
              request.status
            )

          const matchesStatus =
            !statusFilter ||
            currentStatus ===
              statusFilter

          return (
            matchesSearch &&
            matchesStatus
          )
        }
      )
    }, [
      requests,
      searchTerm,
      statusFilter,
    ])


  // =======================================================
  // Render
  // =======================================================

  return (
    <MainLayout>
      <Box
        sx={{
          minWidth: 0,
          pb: { xs: 2, md: 4 },
          "& .MuiPaper-root": {
            borderColor: "rgba(148, 163, 184, 0.24)",
            backgroundImage: "none",
          },
          "& .MuiButton-root": {
            textTransform: "none",
            fontWeight: 800,
            borderRadius: 2.5,
          },
          "& .MuiTextField-root .MuiOutlinedInput-root": {
            borderRadius: 2.5,
          },
          "& .MuiAlert-root": {
            borderRadius: 3,
          },
          "& .MuiTableHead-root .MuiTableCell-root": {
            backgroundColor: "#f8fafc",
            color: "#334155",
            fontWeight: 800,
            whiteSpace: "nowrap",
          },
          "& .MuiTableBody-root .MuiTableRow-root:hover": {
            backgroundColor: "#f8fbff",
          },
          "& .MuiChip-root": {
            fontWeight: 700,
          },
        }}
      >
        {/* ================================================= */}
        {/* TITRE */}
        {/* ================================================= */}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(0, 1fr) auto",
            },
            alignItems: "start",
            columnGap: 2,
            rowGap: 1.25,
            mb: 2.5,
            width: "100%",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h4"
              fontWeight={900}
              sx={{
                color: "#0f172a",
                fontSize: {
                  xs: "1.65rem",
                  sm: "1.95rem",
                  md: "2.15rem",
                },
                lineHeight: 1.12,
                letterSpacing: "-0.02em",
              }}
            >
              Demandes d'achat
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
                maxWidth: 760,
                lineHeight: 1.55,
              }}
            >
              Expression, soumission et validation des besoins d'approvisionnement de l'entreprise.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: {
                xs: "stretch",
                md: "flex-end",
              },
              justifySelf: {
                xs: "stretch",
                md: "end",
              },
              width: {
                xs: "100%",
                md: "auto",
              },
              pt: {
                xs: 0.5,
                md: 0.25,
              },
            }}
          >
            <Button
              variant="contained"
              onClick={openAddDialog}
              sx={{
                width: {
                  xs: "100%",
                  sm: "auto",
                },
                minHeight: 42,
                px: 2.25,
                borderRadius: 2.5,
                whiteSpace: "nowrap",
                boxShadow: "0 8px 20px rgba(37, 99, 235, 0.18)",
              }}
            >
              Nouvelle demande
            </Button>
          </Box>
        </Box>


        {/* ================================================= */}
        {/* WORKFLOW */}
        {/* ================================================= */}


        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(4, minmax(0, 1fr))",
            },
            gap: 1.5,
            mb: 2,
          }}
        >
          <MetricCard
            label="Demandes"
            value={requests.length}
            caption="Total enregistré"
          />
          <MetricCard
            label="À valider"
            value={
              requests.filter(
                (request) =>
                  normalizeStatus(request.status) === "SUBMITTED"
              ).length
            }
            caption="Demandes soumises"
            accent="#f59e0b"
          />
          <MetricCard
            label="Approuvées"
            value={
              requests.filter(
                (request) =>
                  normalizeStatus(request.status) === "APPROVED"
              ).length
            }
            caption="Prêtes pour commande"
            accent="#16a34a"
          />
          <MetricCard
            label="Converties en BC"
            value={
              requests.filter((request) =>
                ["CONVERTED", "CONVERTED_TO_PO"].includes(
                  normalizeStatus(request.status)
                )
              ).length
            }
            caption="Workflow poursuivi"
            accent="#0891b2"
          />
        </Box>



        <Alert
          severity="info"
          sx={{
            mb: 2,
          }}
        >
          Workflow : Brouillon → Soumission → Validation
          interne → Bon de commande fournisseur → Réception
          fournisseur → Mise en stock.
        </Alert>


        {/* ================================================= */}
        {/* FILTRES */}
        {/* ================================================= */}

        <Paper
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 3.5,
          }}
        >
          <Stack
            direction={{
              xs: "column",
              md: "row",
            }}
            spacing={2}
          >
            <TextField
              fullWidth
              placeholder="Rechercher une demande..."
              value={
                searchTerm
              }
              onChange={(
                event
              ) =>
                setSearchTerm(
                  event.target.value
                )
              }
            />

            <TextField
              select
              label="Statut"
              value={
                statusFilter
              }
              onChange={(
                event
              ) =>
                setStatusFilter(
                  event.target.value
                )
              }
              sx={{
                minWidth: 200,
              }}
            >
              <MenuItem value="">
                Tous statuts
              </MenuItem>

              <MenuItem value="DRAFT">
                Brouillon
              </MenuItem>

              <MenuItem value="SUBMITTED">
                Soumise
              </MenuItem>

              <MenuItem value="APPROVED">
                Approuvée
              </MenuItem>

              <MenuItem value="REJECTED">
                Rejetée
              </MenuItem>

              <MenuItem value="CANCELLED">
                Annulée
              </MenuItem>

              <MenuItem value="CONVERTED_TO_PO">
                Convertie en BC
              </MenuItem>
            </TextField>

            <Button
              variant="outlined"
              onClick={() => {
                setSearchTerm(
                  ""
                )

                setStatusFilter(
                  ""
                )
              }}
            >
              Reset
            </Button>
          </Stack>
        </Paper>


        {/* ================================================= */}
        {/* TABLE */}
        {/* ================================================= */}

        <Paper
          sx={{
            borderRadius: 3.5,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              overflowX: "auto",
            }}
          >
            <Table
              sx={{
                minWidth: 1250,
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>
                    Code
                  </TableCell>

                  <TableCell>
                    Entrepôt
                  </TableCell>

                  <TableCell>
                    Motif
                  </TableCell>

                  <TableCell>
                    Date requise
                  </TableCell>

                  <TableCell>
                    Articles
                  </TableCell>

                  <TableCell
                    align="right"
                  >
                    Montant estimé
                  </TableCell>

                  <TableCell>
                    Statut
                  </TableCell>

                  <TableCell>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                    >
                      Chargement...
                    </TableCell>
                  </TableRow>
                ) : filteredRequests.length ===
                  0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                    >
                      Aucune demande d'achat.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRequests
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map(
                    (request) => {
                      const status =
                        normalizeStatus(
                          request.status
                        )

                      const processing =
                        processingId ===
                        request.id

                      return (
                        <TableRow
                          key={
                            request.id
                          }
                          hover
                        >
                          <TableCell>
                            <strong>
                              {request.code ||
                                request.request_number ||
                                `PR-${request.id}`}
                            </strong>
                          </TableCell>

                          <TableCell>
                            <Stack
                              spacing={0.5}
                            >
                              <Typography
                                variant="body2"
                              >
                                {request.warehouse_name ||
                                  warehouseLabel(
                                    request.warehouse_id
                                  )}
                              </Typography>

                              {request.warehouse_type && (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={
                                    formatWarehouseType(
                                      request.warehouse_type
                                    )
                                  }
                                  sx={{
                                    width:
                                      "fit-content",
                                  }}
                                />
                              )}
                            </Stack>
                          </TableCell>

                          <TableCell>
                            {request.reason ||
                              "-"}
                          </TableCell>

                          <TableCell>
                            {request.required_date
                              ? String(
                                  request.required_date
                                ).slice(
                                  0,
                                  10
                                )
                              : "-"}
                          </TableCell>

                          <TableCell>
                            {request.items_count ??
                              request.item_count ??
                              request.items?.length ??
                              "-"}
                          </TableCell>

                          <TableCell
                            align="right"
                          >
                            {Number(
                              request.estimated_total ??
                                request.total_estimated ??
                                0
                            ).toFixed(
                              2
                            )}
                          </TableCell>

                          <TableCell>
                            <Chip
                              size="small"
                              color={
                                statusColor(
                                  status
                                )
                              }
                              label={
                                STATUS_LABELS[
                                  status
                                ] ||
                                status
                              }
                            />
                          </TableCell>

                          <TableCell>
                            <Stack
                              direction="row"
                              spacing={1}
                              flexWrap="wrap"
                              useFlexGap
                              sx={{
                                minWidth:
                                  430,
                              }}
                            >
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() =>
                                  openViewDialog(
                                    request.id
                                  )
                                }
                              >
                                Voir
                              </Button>


                              {/* ========================== */}
                              {/* DRAFT */}
                              {/* ========================== */}

                              {status ===
                                "DRAFT" && (
                                <>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() =>
                                      openEditDialog(
                                        request.id
                                      )
                                    }
                                  >
                                    Modifier
                                  </Button>

                                  <Button
                                    size="small"
                                    variant="contained"
                                    disabled={
                                      processing
                                    }
                                    onClick={() =>
                                      handleSubmitRequest(
                                        request.id
                                      )
                                    }
                                  >
                                    Soumettre
                                  </Button>

                                  <Button
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    disabled={
                                      processing
                                    }
                                    onClick={() =>
                                      handleCancelRequest(
                                        request.id
                                      )
                                    }
                                  >
                                    Annuler
                                  </Button>
                                </>
                              )}


                              {/* ========================== */}
                              {/* SUBMITTED */}
                              {/* ========================== */}

                              {status ===
                                "SUBMITTED" && (
                                <>
                                  {canApprove ? (
                                    <>
                                      <Button
                                        size="small"
                                        color="success"
                                        variant="contained"
                                        disabled={
                                          processing
                                        }
                                        onClick={() =>
                                          handleApprove(
                                            request.id
                                          )
                                        }
                                      >
                                        Approuver
                                      </Button>

                                      <Button
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                        disabled={
                                          processing
                                        }
                                        onClick={() =>
                                          openRejectDialog(
                                            request
                                          )
                                        }
                                      >
                                        Rejeter
                                      </Button>
                                    </>
                                  ) : (
                                    <Chip
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                      label="En attente de validation"
                                    />
                                  )}

                                  <Button
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    disabled={
                                      processing
                                    }
                                    onClick={() =>
                                      handleCancelRequest(
                                        request.id
                                      )
                                    }
                                  >
                                    Annuler
                                  </Button>
                                </>
                              )}


                              {/* ========================== */}
                              {/* APPROVED */}
                              {/* ========================== */}

                              {status ===
                                "APPROVED" && (
                                <>
                                  <Button
                                    size="small"
                                    color="success"
                                    variant="contained"
                                    disabled={
                                      processing
                                    }
                                    onClick={() =>
                                      handleCreatePurchaseOrder(
                                        request
                                      )
                                    }
                                  >
                                    Créer bon de commande
                                  </Button>

                                  {canApprove && (
                                    <Button
                                      size="small"
                                      color="error"
                                      variant="outlined"
                                      disabled={
                                        processing
                                      }
                                      onClick={() =>
                                        handleCancelRequest(
                                          request.id
                                        )
                                      }
                                    >
                                      Annuler
                                    </Button>
                                  )}
                                </>
                              )}


                              {/* ========================== */}
                              {/* CONVERTED */}
                              {/* ========================== */}

                              {[
                                "CONVERTED",
                                "CONVERTED_TO_PO",
                              ].includes(
                                status
                              ) && (
                                <Chip
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  label="Bon de commande créé"
                                />
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      )
                    }
                  )
                )}
              </TableBody>
            </Table>
          </Box>
        

          <TablePagination
            component="div"
            count={filteredRequests.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5]}
            labelRowsPerPage="Lignes"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
              "& .MuiTablePagination-toolbar": {
                minHeight: 54,
                px: { xs: 1, sm: 2 },
              },
            }}
          />
        </Paper>
      </Box>


      {/* =================================================== */}
      {/* CREATION / MODIFICATION */}
      {/* =================================================== */}

      <Dialog
        open={open}
        onClose={
          handleClose
        }
        fullWidth
        maxWidth="lg"
      
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 4 },
            maxHeight: { xs: "100dvh", sm: "90dvh" },
            m: { xs: 0, sm: 2 },
          },
        }}
      >
        <DialogTitle>
          {selectedRequest
            ? "Modifier la demande d'achat"
            : "Nouvelle demande d'achat"}
        </DialogTitle>

        <DialogContent>
          <Stack
            spacing={2.5}
            mt={1}
          >
            <Stack
              direction={{
                xs: "column",
                md: "row",
              }}
              spacing={2}
            >
              <TextField
                select
                label="Organisation"
                name="organization_id"
                value={
                  formData.organization_id
                }
                onChange={
                  handleMainChange
                }
                fullWidth
              >
                {organizations.length ? (
                  organizations.map(
                    (
                      organization
                    ) => (
                      <MenuItem
                        key={
                          organization.id
                        }
                        value={
                          organization.id
                        }
                      >
                        {
                          organization.name
                        }
                      </MenuItem>
                    )
                  )
                ) : (
                  <MenuItem
                    value={1}
                  >
                    Organisation principale
                  </MenuItem>
                )}
              </TextField>


              <TextField
                select
                required
                label="Entrepôt"
                name="warehouse_id"
                value={
                  formData.warehouse_id
                }
                onChange={
                  handleMainChange
                }
                fullWidth
              >
                {warehouses.map(
                  (
                    warehouse
                  ) => (
                    <MenuItem
                      key={
                        warehouse.id
                      }
                      value={
                        warehouse.id
                      }
                    >
                      {warehouse.code
                        ? `${warehouse.code} - ${warehouse.name} (${formatWarehouseType(
                            warehouse.warehouse_type
                          )})`
                        : warehouse.name}
                    </MenuItem>
                  )
                )}
              </TextField>


              <TextField
                type="date"
                label="Date requise"
                name="required_date"
                value={
                  formData.required_date
                }
                onChange={
                  handleMainChange
                }
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
              />
            </Stack>


            {selectedWarehouse && (
              <Alert
                severity={
                  isRawMaterialWarehouse(
                    selectedWarehouse.warehouse_type
                  )
                    ? "warning"
                    : "info"
                }
              >
                Entrepôt sélectionné :{" "}
                <strong>
                  {selectedWarehouse.code} -{" "}
                  {selectedWarehouse.name}
                </strong>
                {" • "}
                {formatWarehouseType(
                  selectedWarehouse.warehouse_type
                )}.
                {" "}
                Les articles proposés ci-dessous sont
                filtrés selon leur rôle métier. Un entrepôt
                matières peut recevoir des matières premières,
                composants, emballages et consommables achetables.
              </Alert>
            )}


            <TextField
              label="Motif / justification"
              name="reason"
              value={
                formData.reason
              }
              onChange={
                handleMainChange
              }
              fullWidth
              required
            />


            <TextField
              multiline
              minRows={2}
              label="Notes"
              name="notes"
              value={
                formData.notes
              }
              onChange={
                handleMainChange
              }
              fullWidth
            />


            <Divider />


            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              justifyContent="space-between"
              alignItems={{
                xs: "stretch",
                sm: "center",
              }}
              spacing={1}
            >
              <Typography
                variant="h6"
              >
                Produits demandés
              </Typography>

              <Button
                startIcon={
                  <AddRoundedIcon />
                }
                onClick={
                  addItem
                }
                disabled={
                  !formData.warehouse_id
                }
              >
                Ajouter produit
              </Button>
            </Stack>


            {!formData.warehouse_id && (
              <Alert
                severity="info"
              >
                Sélectionnez d'abord l'entrepôt de
                destination afin que RLSys affiche les
                produits compatibles.
              </Alert>
            )}


            {formData.items.map(
              (
                item,
                index
              ) => {
                const selectedProduct =
                  products.find(
                    (product) =>
                      Number(
                        product.id
                      ) ===
                      Number(
                        item.product_id
                      )
                  )

                return (
                  <Paper
                    key={
                      index
                    }
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 3.5,
                    }}
                  >
                    <Stack
                      direction={{
                        xs: "column",
                        md: "row",
                      }}
                      spacing={2}
                      alignItems={{
                        xs: "stretch",
                        md: "center",
                      }}
                    >
                      <TextField
                        select
                        required
                        label="Produit"
                        value={
                          item.product_id
                        }
                        disabled={
                          !formData.warehouse_id
                        }
                        onChange={(
                          event
                        ) =>
                          handleItemChange(
                            index,
                            "product_id",
                            event.target.value
                          )
                        }
                        fullWidth
                      >
                        {availableProducts.map(
                          (
                            product
                          ) => (
                            <MenuItem
                              key={
                                product.id
                              }
                              value={
                                product.id
                              }
                            >
                              {product.code
                                ? `${product.code} - ${product.name}`
                                : product.name}

                              {product.unit
                                ? ` • ${product.unit}`
                                : ""}
                            </MenuItem>
                          )
                        )}
                      </TextField>


                      <TextField
                        type="number"
                        label={
                          selectedProduct?.unit
                            ? `Quantité (${selectedProduct.unit})`
                            : "Quantité"
                        }
                        value={
                          item.requested_quantity
                        }
                        onChange={(
                          event
                        ) =>
                          handleItemChange(
                            index,
                            "requested_quantity",
                            event.target.value
                          )
                        }
                        inputProps={{
                          min: 0.0001,
                          step: "any",
                        }}
                        sx={{
                          minWidth: 170,
                        }}
                      />


                      <TextField
                        type="number"
                        label="Coût unitaire estimé"
                        value={
                          item.estimated_unit_cost
                        }
                        onChange={(
                          event
                        ) =>
                          handleItemChange(
                            index,
                            "estimated_unit_cost",
                            event.target.value
                          )
                        }
                        inputProps={{
                          min: 0,
                          step: "0.01",
                        }}
                        sx={{
                          minWidth: 190,
                        }}
                      />


                      <TextField
                        label="Notes"
                        value={
                          item.notes
                        }
                        onChange={(
                          event
                        ) =>
                          handleItemChange(
                            index,
                            "notes",
                            event.target.value
                          )
                        }
                        fullWidth
                      />


                      <IconButton
                        color="error"
                        disabled={
                          formData.items.length ===
                          1
                        }
                        onClick={() =>
                          removeItem(
                            index
                          )
                        }
                        aria-label="Supprimer le produit"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Paper>
                )
              }
            )}


            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor:
                  "background.default",
                borderRadius: 3.5,
              }}
            >
              <Typography
                fontWeight="bold"
              >
                Total estimatif :{" "}
                {estimatedTotal.toFixed(
                  2
                )}
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Pour une matière première, ce montant
                reste indicatif. Le véritable prix
                d'achat sera déterminé lors du bon de
                commande fournisseur.
              </Typography>
            </Paper>


            <Alert
              severity="info"
            >
              La création ou l'approbation de cette
              demande ne modifie pas le stock. Le stock
              de l'entrepôt sera augmenté uniquement
              après réception et contrôle de la livraison
              fournisseur.
            </Alert>
          </Stack>
        </DialogContent>


        <DialogActions
          sx={{
            px: 3,
            pb: 2,
          }}
        >
          <Button
            onClick={
              handleClose
            }
            disabled={
              saving
            }
          >
            Annuler
          </Button>

          <Button
            variant="contained"
            disabled={
              saving
            }
            onClick={
              handleSave
            }
          >
            {saving
              ? "Enregistrement..."
              : selectedRequest
                ? "Modifier"
                : "Enregistrer"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* =================================================== */}
      {/* VOIR DETAIL */}
      {/* =================================================== */}

      <Dialog
        open={viewOpen}
        onClose={() =>
          setViewOpen(
            false
          )
        }
        fullWidth
        maxWidth="md"
      
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 4 },
            maxHeight: { xs: "100dvh", sm: "90dvh" },
            m: { xs: 0, sm: 2 },
          },
        }}
      >
        <DialogTitle>
          Détail de la demande d'achat
        </DialogTitle>

        <DialogContent>
          {viewLoading ? (
            <Typography>
              Chargement...
            </Typography>
          ) : !viewData ? (
            <Typography>
              Aucune donnée.
            </Typography>
          ) : (
            <Stack
              spacing={2}
              mt={1}
            >
              <Stack
                direction={{
                  xs: "column",
                  md: "row",
                }}
                spacing={2}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    flex: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Code
                  </Typography>

                  <Typography
                    fontWeight="bold"
                  >
                    {viewData.code}
                  </Typography>
                </Paper>


                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    flex: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Statut
                  </Typography>

                  <Box
                    mt={0.5}
                  >
                    <Chip
                      size="small"
                      color={
                        statusColor(
                          viewData.status
                        )
                      }
                      label={
                        STATUS_LABELS[
                          normalizeStatus(
                            viewData.status
                          )
                        ] ||
                        normalizeStatus(
                          viewData.status
                        )
                      }
                    />
                  </Box>
                </Paper>
              </Stack>


              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Organisation
                </Typography>

                <Typography>
                  {viewData.organization_name ||
                    "-"}
                </Typography>


                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mt={2}
                >
                  Entrepôt de destination
                </Typography>

                <Typography>
                  {viewData.warehouse_code
                    ? `${viewData.warehouse_code} - ${viewData.warehouse_name}`
                    : viewData.warehouse_name ||
                      "-"}
                </Typography>

                {viewData.warehouse_type && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={
                      formatWarehouseType(
                        viewData.warehouse_type
                      )
                    }
                    sx={{
                      mt: 1,
                    }}
                  />
                )}
              </Paper>


              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Motif
                </Typography>

                <Typography>
                  {viewData.reason ||
                    "-"}
                </Typography>

                {viewData.notes && (
                  <>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mt={2}
                    >
                      Notes
                    </Typography>

                    <Typography>
                      {
                        viewData.notes
                      }
                    </Typography>
                  </>
                )}
              </Paper>


              <Typography
                variant="h6"
              >
                Produits demandés
              </Typography>


              <Paper
                variant="outlined"
                sx={{
                  overflowX:
                    "auto",
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        Produit
                      </TableCell>

                      <TableCell>
                        Nature
                      </TableCell>

                      <TableCell>
                        Quantité demandée
                      </TableCell>

                      <TableCell>
                        Quantité approuvée
                      </TableCell>

                      <TableCell>
                        Stock actuel
                      </TableCell>

                      <TableCell
                        align="right"
                      >
                        Coût estimé
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {Array.isArray(
                      viewData.items
                    ) &&
                      viewData.items.map(
                        (
                          item
                        ) => (
                          <TableRow
                            key={
                              item.id
                            }
                          >
                            <TableCell>
                              {item.product_code
                                ? `${item.product_code} - ${item.product_name}`
                                : item.product_name ||
                                  productLabel(
                                    item.product_id
                                  )}
                            </TableCell>

                            <TableCell>
                              {formatProductType(
                                item.product_type
                              )}
                            </TableCell>

                            <TableCell>
                              {formatQuantity(
                                item.requested_quantity,
                                item.unit
                              )}
                            </TableCell>

                            <TableCell>
                              {item.approved_quantity ===
                                null ||
                              item.approved_quantity ===
                                undefined
                                ? "-"
                                : formatQuantity(
                                    item.approved_quantity,
                                    item.unit
                                  )}
                            </TableCell>

                            <TableCell>
                              {formatQuantity(
                                item.current_stock,
                                item.unit
                              )}
                            </TableCell>

                            <TableCell
                              align="right"
                            >
                              {Number(
                                item.estimated_unit_cost ||
                                  0
                              ).toFixed(
                                2
                              )}
                            </TableCell>
                          </TableRow>
                        )
                      )}
                  </TableBody>
                </Table>
              </Paper>


              {normalizeStatus(
                viewData.status
              ) ===
                "APPROVED" && (
                <Alert
                  severity="success"
                >
                  Cette demande a été approuvée. Elle peut
                  maintenant être convertie en bon de
                  commande fournisseur. Aucun stock n'a
                  encore été ajouté.
                </Alert>
              )}


              {normalizeStatus(
                viewData.status
              ) ===
                "SUBMITTED" && (
                <Alert
                  severity="warning"
                >
                  Cette demande attend une validation
                  interne.
                </Alert>
              )}


              {viewData.rejection_reason && (
                <Alert
                  severity="error"
                >
                  Motif du rejet :{" "}
                  {
                    viewData.rejection_reason
                  }
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() =>
              setViewOpen(
                false
              )
            }
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>


      {/* =================================================== */}
      {/* REJET */}
      {/* =================================================== */}

      <Dialog
        open={rejectOpen}
        onClose={
          closeRejectDialog
        }
        fullWidth
        maxWidth="sm"
      
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 4 },
            maxHeight: { xs: "100dvh", sm: "90dvh" },
            m: { xs: 0, sm: 2 },
          },
        }}
      >
        <DialogTitle>
          Rejeter la demande d'achat
        </DialogTitle>

        <DialogContent>
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              mt: 1,
            }}
          >
            Cette action marquera la demande{" "}
            <strong>
              {rejectTarget?.code}
            </strong>{" "}
            comme rejetée.
          </Alert>

          <TextField
            autoFocus
            fullWidth
            required
            multiline
            minRows={3}
            label="Motif du rejet"
            value={
              rejectionReason
            }
            onChange={(
              event
            ) =>
              setRejectionReason(
                event.target.value
              )
            }
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={
              closeRejectDialog
            }
            disabled={
              Boolean(
                processingId
              )
            }
          >
            Retour
          </Button>

          <Button
            color="error"
            variant="contained"
            disabled={
              Boolean(
                processingId
              )
            }
            onClick={
              handleReject
            }
          >
            Confirmer le rejet
          </Button>
        </DialogActions>
      </Dialog>


      {/* =================================================== */}
      {/* SNACKBAR */}
      {/* =================================================== */}

      <Snackbar
        open={
          snackbar.open
        }
        autoHideDuration={
          3500
        }
        onClose={() =>
          setSnackbar(
            (prev) => ({
              ...prev,
              open: false,
            })
          )
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          severity={
            snackbar.severity
          }
          variant="filled"
          onClose={() =>
            setSnackbar(
              (prev) => ({
                ...prev,
                open: false,
              })
            )
          }
        >
          {
            snackbar.message
          }
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}


export default PurchaseRequestsPage